import type { ManualResponseStatus } from "@platform/api";
import type { ColDef } from "ag-grid-community";

/**
 * Display-layer status label shown in each cell of the responses grid.
 *
 * Derived from the combination of `ProposalStatus` + `CoverageStatus` from
 * the centro-portal API response (see `buildResponsesGridData.ts`), or from
 * `manualStatus` when an analyst has explicitly set a value.
 */
export type DisplayStatus =
  | "Incumbent"
  | "Proposal Received"
  | "DTQ"
  | "Pending Proposal"
  | "Pending"
  | "Withdrawn"
  | "Not Requested"
  | "Not Supported"
  | "Not Applicable"
  | "Not Quoted";

/**
 * The ordered list of values an analyst can select from the cell dropdown.
 * Typed as `ManualResponseStatus[]` so only contract-valid values can be listed.
 * If the enum changes, update this list to match the desired UI order.
 */
export const MANUAL_RESPONSE_OPTIONS: readonly ManualResponseStatus[] = [
  "Incumbent",
  "Pending Proposal",
  "Not Requested",
  "Proposal Received",
  "DTQ",
  "Not Supported",
  "Not Applicable",
  "Not Quoted",
] as const;

/**
 * A single row in the responses grid.
 *
 * `carrierId` and `carrierName` are fixed fields; all other keys are dynamic
 * coverage column keys (matching `CoverageRef.planGroupId`) with
 * `DisplayStatus` values.
 */
export interface ResponsesGridRow {
  carrierId: string;
  carrierName: string;
  isIncumbent?: boolean;
  [coverageKey: string]: DisplayStatus | string | boolean | undefined;
}

export type ResponsesColDef = ColDef<ResponsesGridRow>;
